<template>
    <div
        :class="{
            [$style['select']]: true,
            [$style['select_roundings_none']]: $simlaweb.settings.roundings === ROUNDINGS.NONE,
            [$style['select_roundings_small']]: $simlaweb.settings.roundings === ROUNDINGS.SMALL,
            [$style['select_roundings_large']]: $simlaweb.settings.roundings === ROUNDINGS.LARGE
        }"
    >
        <div
            ref="header"
            :class="{
                [$style['header']]: true,
                [$style['header_active']]: expanded,
            }"
        >
            <div :class="[$style['header__text'], 'sw-text']">
                {{ selection ? selection.name : '' }}
            </div>

            <SvgIconCaret :class="$style['header__caret']" />
        </div>

        <div
            v-show="expanded"
            :class="{
                'sw-popup': true,
                [$style['select__popup']]: true,
                [popupClass]: popupClass
            }"
        >
            <div
                v-for="(item) in variants"
                :key="item.id"
                :class="{
                    [$style['select__item']]: true,
                    [$style['select__item_selected']]: item.id === sorting
                }"
                @click="change(item.id)"
            >
                <div class="sw-text">
                    {{ item.name }}
                </div>

                <SvgIconCheckMark v-if="item.id === sorting" />
            </div>
        </div>
    </div>
</template>

<script>
import SvgIconCaret from '@@/framework/icons/caret-down.svg'
import SvgIconCheckMark from '@@/framework/icons/check-mark.svg'

import { SortType, SortMethodsAvailable } from '@@/website/store/catalog'
import { ROUNDINGS } from '@@/framework/types'

export default {
  name: 'SortSelect',

  components: {
    SvgIconCaret,
    SvgIconCheckMark
  },

  props: {
    sorting: {
      type: String,
      default: SortType.New
    },

    popupClass: {
      type: String,
      default: ''
    }
  },

  data: () => ({
    expanded: false
  }),

  computed: {
    ROUNDINGS: () => ROUNDINGS,

    selection () {
      return this.variants.find(item => item.id === this.sorting)
    },

    /** @return {Array<SortMethod & { name: string }>} */
    variants () {
      return [
        { ...SortMethodsAvailable[SortType.New], name: this.$t('sort_novelty') },
        { ...SortMethodsAvailable[SortType.Max], name: this.$t('sort_descending') },
        { ...SortMethodsAvailable[SortType.Min], name: this.$t('sort_ascending') }
      ]
    },
  },

  mounted () {
    this.change(this.sorting)
    const onMissClick = (event) => {
      if (
        this.$refs.header === event.target ||
        this.$refs.header.contains(event.target)
      ) {
        this.expanded = !this.expanded
      } else {
        this.expanded = false
      }
    }

    document.addEventListener('click', onMissClick)
    this.$once('hook:destroyed', () => {
      document.removeEventListener('click', onMissClick)
    })
  },

  methods: {
    async change (id) {
      this.expanded = false

      const sortId = this.$store.state.catalog.sort?.id
      const selection = this.variants.find(item => item.id === id) || this.variants[0]

      this.$emit('change', selection)

      if (!this.$store.state.admin || this.$route.path.includes('preview')) {
        const query = { ...this.$route.query }

        if (sortId && sortId !== selection.id) {
          delete query.page
        }

        if (selection.id === this.$store.state.catalog.defaultSort) {
          delete query.sort

          await this.$router.push({ path: this.$route.path, query })
        } else {
          await this.$router.push({ path: this.$route.path, query: { ...query, sort: selection.id } })
        }
      }
    }
  }
}
</script>

<style lang="less" module>
@import (reference) "./assets/Web.less";

.header {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: fit-content;
  font-size: 14px;
  font-weight: 400;
  line-height: 40px;
  cursor: pointer;

  &_active {
    color: var(--sw-color-main-500);
  }

  &__text {
    font-size: 14px;
    line-height: 20px;
  }

  &__caret {
    margin-left: 8px;
  }

  &_active &__caret {
    transform: rotate(180deg)
  }
}

.select {
  position: relative;
  max-width: 200px;

  &__popup {
    position: absolute;
    padding: 8px;
    min-width: 184px;
    z-index: 100;
    right: 0;
  }

  &_roundings_none &__popup { border-radius: 0; }
  &_roundings_small &__popup { border-radius: 8px; }
  &_roundings_large &__popup { border-radius: 12px; }

  &__item {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
    padding: 6px 8px;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    cursor: pointer;

    & + & {
      margin-top: 4px;
    }

    &:hover {
      background: @brand-100;
      border-radius: 6px;
    }

    &_selected {
      color: @brand-500;
    }

    & svg {
      color: #12B886;
      margin-left: 16px;
    }
  }

  &_roundings_none &__dropdown { border-radius: 0; }
  &_roundings_small &__dropdown { border-radius: 6px; }
  &_roundings_large &__dropdown { border-radius: 8px; }
}
</style>
